import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Highcharts from 'highcharts';
import HighchartsDrilldown from 'highcharts/modules/drilldown';
import { Box, Typography, Button } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import {
  formatDaysHours,
  getUniqueDealerships,
  getDealershipWorkFlowAverageForRange,
  getGroupWorkFlowAverageForRange,
  getDealershipWorkFlowVehicleCount,
  groupVehiclesByDepartments,
} from './chartUtils';

// Initialize drilldown module
HighchartsDrilldown(Highcharts);

const GroupWorkFlowAveragesChart = ({ range, reportDepartmentData, dealershipReportHours, work_flow_id }) => {
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [visibleDealers, setVisibleDealers] = useState(null);
  const { workflows, report_vehicles, departments } = useSelector(state => ({
    workflows: state.settings_departments.work_flows,
    report_vehicles: state.settings_reports.report_vehicles,
    departments: state.settings_departments.departments,
  }));

  // Define a color palette for unique dealership colors
  const colorPalette = [
    '#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8',
    '#6610f2', '#fd7e14', '#6f42c1', '#e83e8c', '#20c997',
  ];

  // Get workflow name
  const workflowName = useMemo(() => {
    const workflow = workflows.find(w => parseInt(w.id) === parseInt(work_flow_id));
    return workflow ? (workflow.name || 'Unknown') : 'Unknown';
  }, [workflows, work_flow_id]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!range || !reportDepartmentData || !Array.isArray(reportDepartmentData)) {
      console.warn(`No data for ${range || 'null'}-day chart: missing range or reportDepartmentData`, { range, reportDepartmentData });
      return { categories: [], dataPoints: [], vehicleCounts: [], groupAverage: null };
    }
    const uniqueDealerships = getUniqueDealerships(reportDepartmentData);
    console.log(`Unique dealerships for range ${range}:`, uniqueDealerships);
    const dealershipAverages = uniqueDealerships.map((dealership, index) => {
      const average = getDealershipWorkFlowAverageForRange(dealership, range, reportDepartmentData, dealershipReportHours, work_flow_id);
      const vehicleCount = getDealershipWorkFlowVehicleCount(dealership, range, reportDepartmentData, work_flow_id);
      console.log(`Dealership ${dealership} (workflow ${work_flow_id}): average=${average}, count=${vehicleCount}`);
      return {
        name: dealership,
        average,
        color: colorPalette[index % colorPalette.length],
        vehicleCount,
      };
    });
    const validAverages = dealershipAverages.filter(item => item.average !== null && item.average > 0 && item.name);
    const categories = validAverages.map(item => item.name);
    const dataPoints = validAverages.map((item, index) => ({
      name: item.name,
      y: Number(item.average) || 0,
      color: item.color,
      drilldown: item.name, // Enable drilldown for each dealership
    }));
    const vehicleCounts = validAverages.map(item => item.vehicleCount || 0);
    const groupAverage = getGroupWorkFlowAverageForRange(range, reportDepartmentData, dealershipReportHours, null, work_flow_id);
    console.log(`Chart data for ${range}-day range, workflow ${work_flow_id}, hours setting: ${dealershipReportHours}`, { categories, dataPoints, vehicleCounts, groupAverage });
    return { categories, dataPoints, vehicleCounts, groupAverage };
  }, [range, reportDepartmentData, dealershipReportHours, work_flow_id]);

  // Prepare drilldown series
  const drilldownSeries = useMemo(() => {
    if (!report_vehicles || !chartData.categories.length) return [];

    return chartData.categories.map(dealership => {
      const deptData = groupVehiclesByDepartments(report_vehicles, departments, work_flow_id, dealership);
      return {
        id: dealership,
        name: `Departments in ${dealership}`,
        data: deptData.map(dept => ({
          name: dept.name,
          y: Number(dept.average_time) || 0,
          vehicleCount: dept.vehicle_count,
        })),
      };
    });
  }, [report_vehicles, departments, work_flow_id, chartData.categories]);

  // Initialize visibleDealers
  useEffect(() => {
    if (!visibleDealers && chartData.categories.length > 0) {
      const initialVisible = chartData.categories.reduce((acc, dealer) => ({ ...acc, [dealer]: true }), {});
      setVisibleDealers(initialVisible);
    } else if (!visibleDealers && chartData.categories.length === 0) {
      setVisibleDealers({});
    }
  }, [chartData.categories, visibleDealers]);

  // Filter chart data based on visible dealers
  const filteredChartData = useMemo(() => {
    if (!chartData.categories.length || !visibleDealers) {
      return chartData;
    }
    const filteredCategories = chartData.categories.filter((_, index) => visibleDealers[chartData.categories[index]]);
    const filteredDataPoints = chartData.dataPoints.filter((_, index) => visibleDealers[chartData.categories[index]]);
    const filteredVehicleCounts = chartData.vehicleCounts.filter((_, index) => visibleDealers[chartData.categories[index]]);
    const filteredAverage = getGroupWorkFlowAverageForRange(range, reportDepartmentData, dealershipReportHours, visibleDealers, work_flow_id);
    console.log(`Filtered chart data for ${range}-day range:`, { filteredCategories, filteredDataPoints, filteredVehicleCounts, filteredAverage });
    return {
      categories: filteredCategories,
      dataPoints: filteredDataPoints,
      vehicleCounts: filteredVehicleCounts,
      groupAverage: filteredAverage,
    };
  }, [chartData, visibleDealers, range, reportDepartmentData, dealershipReportHours, work_flow_id]);

  const toggleDealer = (dealer) => {
    setVisibleDealers(prev => ({
      ...prev,
      [dealer]: !prev[dealer],
    }));
  };

  // Export to PDF
  const handleExportToPDF = () => {
    const doc = new jsPDF();
    const now = new Date();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const year = now.getFullYear();
    const filename = `group_averages_${range}_${month}_${day}_${year}.pdf`;

    // Add title
    doc.setFontSize(16);
    doc.text(`${range}-day Group Averages (${workflowName})`, 10, 10);

    // Capture chart
    const element = containerRef.current;
    if (element) {
      html2canvas(element).then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        const imgProps = doc.getImageProperties(imgData);
        const pdfWidth = doc.internal.pageSize.getWidth() - 20;
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        doc.addImage(imgData, 'PNG', 10, 20, pdfWidth, pdfHeight);

        // Add summary table
        if (filteredChartData.categories.length > 0) {
          const tableColumn = ['Dealership', 'Workflow', 'Average Time', 'Vehicle Count'];
          const tableRows = filteredChartData.categories.map((dealer, index) => [
            dealer,
            workflowName,
            formatDaysHours(filteredChartData.dataPoints[index]?.y) || 'N/A',
            filteredChartData.vehicleCounts[index] || 0,
          ]);
          autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20 + pdfHeight + 10,
            styles: { fontSize: 10 },
          });
        }

        // Add group average
        if (filteredChartData.groupAverage) {
          const finalY = doc.lastAutoTable.finalY || 20 + pdfHeight + 10;
          doc.setFontSize(12);
          doc.text(`Group Average: ${formatDaysHours(filteredChartData.groupAverage)}`, 10, finalY + 10);
        }

        doc.save(filename);
      }).catch(error => {
        console.error('Error exporting chart to PDF:', error);
      });
    } else {
      console.warn('Chart container not found for PDF export');
    }
  };

  // Validate range
  const isValidRange = range && (['7', '14', '30', '90'].includes(range) || /^[0-9]{4}_[0-9]{2}$/.test(range));
  if (!isValidRange) {
    console.warn(`Invalid range for GroupWorkFlowAveragesChart:`, { range });
    return <Typography>Invalid time range</Typography>;
  }

  useEffect(() => {
    if (containerRef.current) {
      try {
        if (filteredChartData.categories.length === 0) {
          console.warn('No categories to render chart', { filteredChartData });
          chartRef.current = Highcharts.chart(containerRef.current, {
            chart: { type: 'column', backgroundColor: null },
            credits: { enabled: false },
            title: { text: `${range}-day Averages by Dealership`, style: { color: '#000' } },
            xAxis: { categories: ['No Data'], labels: { style: { color: '#000' } } },
            yAxis: {
              min: 0,
              title: { text: 'Average Time', style: { color: '#000' } },
              labels: {
                formatter: function () { return formatDaysHours(this.value); },
                style: { color: '#000' },
              },
            },
            series: [{ name: 'Average Time', data: [0] }],
            legend: { enabled: false },
            drilldown: { series: [] },
          });
          return;
        }

        if (chartRef.current) {
          chartRef.current.series[0].setData(filteredChartData.dataPoints);
          chartRef.current.xAxis[0].setCategories(filteredChartData.categories);
          chartRef.current.yAxis[0].removePlotLine('average-line');
          if (filteredChartData.groupAverage) {
            chartRef.current.yAxis[0].addPlotLine({
              id: 'average-line',
              value: Number(filteredChartData.groupAverage),
              color: '#ff0000',
              width: 2,
              label: {
                text: `Group Average: ${formatDaysHours(filteredChartData.groupAverage)}`,
                align: 'center',
                style: { color: '#ff0000' },
              },
            });
          }
        } else {
          chartRef.current = Highcharts.chart(containerRef.current, {
            chart: {
              type: 'column',
              backgroundColor: null,
              events: {
                drilldown: function (e) {
                  if (!e.seriesOptions) {
                    const chart = this;
                    chart.showLoading('Loading department data...');
                    setTimeout(() => {
                      chart.hideLoading();
                      const series = drilldownSeries.find(s => s.id === e.point.name);
                      if (series) {
                        chart.addSeriesAsDrilldown(e.point, series);
                      }
                    }, 500);
                  }
                },
              },
            },
            credits: {
              enabled: false,
            },
            title: {
              text: `${range}-day Averages by Dealership`,
              style: { color: '#000' },
            },
            xAxis: {
              categories: filteredChartData.categories,
              crosshair: true,
              labels: {
                style: { color: '#000' },
              },
            },
            yAxis: {
              min: 0,
              title: {
                text: 'Average Time',
                style: { color: '#000' },
              },
              plotLines: filteredChartData.groupAverage ? [{
                id: 'average-line',
                value: Number(filteredChartData.groupAverage),
                color: '#ff0000',
                width: 2,
                label: {
                  text: `Group Average: ${formatDaysHours(filteredChartData.groupAverage)}`,
                  align: 'center',
                  style: { color: '#ff0000' },
                },
              }] : [],
              labels: {
                formatter: function () {
                  return formatDaysHours(this.value);
                },
                style: { color: '#000' },
              },
            },
            tooltip: {
              formatter: function () {
                const color = this.series?.color || '#000';
                const name = this.series?.name || 'Average Time';
                const x = this.x || '';
                const y = this.y;
                const vehicleCount = this.point.vehicleCount || filteredChartData.vehicleCounts[this.point.index] || 0;
                return `<span style="font-size:10px">${x}</span><table>` +
                  `<tr><td style="color:${color};padding:0">${name}: </td>` +
                  `<td style="padding:0"><b>${formatDaysHours(y)}</b></td></tr>` +
                  `<tr><td style="color:${color};padding:0">Vehicles: </td>` +
                  `<td style="padding:0"><b>${vehicleCount}</b></td></tr></table>`;
              },
              shared: true,
              useHTML: true,
            },
            plotOptions: {
              column: {
                pointPadding: 0.2,
                borderWidth: 0,
                dataLabels: {
                  enabled: true,
                  rotation: -90,
                  color: '#000',
                  align: 'right',
                  formatter: function () {
                    return formatDaysHours(this.y);
                  },
                  y: 10,
                  style: {
                    fontSize: '13px',
                    color: '#000',
                    fontFamily: 'Verdana, sans-serif',
                  },
                },
              },
            },
            series: [{
              name: 'Average Time',
              data: filteredChartData.dataPoints,
            }],
            legend: {
              enabled: false,
            },
            drilldown: {
              series: drilldownSeries,
            },
          });
        }
      } catch (error) {
        console.error('Error rendering Highcharts:', error, { filteredChartData });
      }
    } else {
      console.warn('Chart not rendered: container missing', { containerExists: !!containerRef.current });
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [filteredChartData, drilldownSeries]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '600px', width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '8px' }}>
        <Button
          onClick={handleExportToPDF}
          startIcon={<FileDownloadIcon />}
          variant="contained"
          color="primary"
          size="small"
        >
          Export to PDF
        </Button>
      </Box>
      <Box sx={{ flex: '1', minHeight: 0 }}>
        <div id="group-chart" ref={containerRef} style={{ height: '100%', width: '100%' }} />
      </Box>
      <Box sx={{ flex: '1', minHeight: 0, padding: '8px' }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '8px' }}>Dealership Key</Typography>
        {chartData.categories.length > 0 ? (
          chartData.categories.map((dealer, index) => (
            <Box
              key={dealer}
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '4px',
                opacity: visibleDealers?.[dealer] ? 1 : 0.5,
                cursor: 'pointer',
              }}
              onClick={() => toggleDealer(dealer)}
            >
              <Box
                sx={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: chartData.dataPoints[index]?.color || '#000',
                  marginRight: '8px',
                  borderRadius: '2px',
                }}
              />
              <Typography variant="body2" sx={{ flex: '1' }}>
                {`${dealer}`}
              </Typography>
              <Typography variant="body2" sx={{ flex: '1' }}>
                {workflowName}
              </Typography>
              <Typography variant="body2" sx={{ flex: '1' }}>
                {`${formatDaysHours(chartData.dataPoints[index]?.y) || 'N/A'}`}
              </Typography>
              <Typography variant="body2" sx={{ flex: '1' }}>
                {`${chartData.vehicleCounts[index] || 0} vehicles`}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography>No dealership data available</Typography>
        )}
      </Box>
    </Box>
  );
};

export default GroupWorkFlowAveragesChart;